import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell } from '@anytag/shared-atoms';
import { formatIntNumber } from '@anytag/shared-utils';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { SocialAccountStatus, SponsoredPostSortField, InfluencerSponsoredPost } from '@anytag/graphql';

interface Props {
  status?: SocialAccountStatus;
}

export const useInfluencerSponsoredPostsListTable = ({ status }: Props) => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();

  const columns: ColumnDef<InfluencerSponsoredPost>[] = [
    {
      accessorKey: 'post',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => {
        const { id, content, thumbNail, postUrl, socialMedia } = row.original;

        return (
          <TableCell.Post
            postUrl={postUrl}
            title={content || ''}
            thumbnail={thumbNail || ''}
            socialPostType={socialMedia}
            onClick={() => setPostDetailsParam(id)}
          />
        );
      }
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: { align: 'right', help: t<string>('Tooltip.ViewHelp'), sortField: SponsoredPostSortField.VIEW },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'reach',
      header: t<string>(status === SocialAccountStatus.SIGNED_UP ? 'HeaderColumn.Reach' : 'HeaderColumn.Est Reach'),
      meta: { align: 'right', help: t<string>('Tooltip.ReachPostHelp'), sortField: SponsoredPostSortField.REACH },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.reaches)} />
    },
    {
      accessorKey: 'likes',
      header: t<string>('HeaderColumn.Like'),
      meta: { align: 'right', help: t<string>('Tooltip.LikeHelp'), sortField: SponsoredPostSortField.LIKE },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.likes)} />
    },
    {
      accessorKey: 'comments',
      header: t<string>('HeaderColumn.Comment'),
      meta: { align: 'right', help: t<string>('Tooltip.CommentHelp'), sortField: SponsoredPostSortField.COMMENT },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'shares',
      header: t<string>('HeaderColumn.share'),
      meta: { align: 'right', help: t<string>('Tooltip.shareHelp'), sortField: SponsoredPostSortField.SHARE },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.shares)} />
    },
    {
      accessorKey: 'saved',
      header: t<string>('HeaderColumn.Save'),
      meta: { align: 'right', help: t<string>('Tooltip.SaveHelp'), sortField: SponsoredPostSortField.SAVED },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.saved)} />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      meta: { help: t<string>('Tooltip.PostDateHelp'), sortField: SponsoredPostSortField.POST_DATE },
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.postedDate} />
    }
  ];

  return { columns };
};
